<script lang="ts" setup>
  import { useEmitter } from '@/composable/useEmitter'
  import { useBuilderStore } from '@/store/builder'
  import { ELEMENTS_META, ELEMENTS_STYLES, PAGE_SECTIONS, Switch } from '@gohighlevel/ghl-proposals-common'
  import { UICheckbox, UIDivider, UIRadio, UITextSmMedium, UITextSmNormal } from '@gohighlevel/ghl-ui'
  import { computed, onBeforeUnmount, ref, watch } from 'vue'
  import { useElementOption } from '../../../composable/elementOptions.js'
  import ColorPickerComponent from '../../common/ColorPickerComponent.vue'

  const emitter = useEmitter()
  const store = useBuilderStore()

  emitter.on('media-library-selected', onSelectImage)
  onBeforeUnmount(() => {
    emitter.off('media-library-selected', onSelectImage)
  })

  const showPageNumbers = computed(() => store.enablePageNumbering)

  const { updateSectionStyles, updateSectionOption } = useElementOption()

  // Get header and footer sections from the store's pages
  const headerSection = computed(() => {
    return store.pages.find(page => page.component?.options?.[PAGE_SECTIONS.HEADERS])?.component?.options?.[PAGE_SECTIONS.HEADERS]
  })

  const footerSection = computed(() => {
    return store.pages.find(page => page.component?.options?.[PAGE_SECTIONS.FOOTERS])?.component?.options?.[PAGE_SECTIONS.FOOTERS]
  })

  const bgColorHeader = ref(headerSection.value?.responsiveStyles?.large?.backgroundColor || '#ffffff')
  const bgColorFooter = ref(footerSection.value?.responsiveStyles?.large?.backgroundColor || '#ffffff')

  const onlyFirstPageHeader = ref(headerSection.value?.onlyFirstPage || false)
  const onlyFirstPageFooter = ref(footerSection.value?.onlyFirstPage || false)

  const selectedLocation = ref(store.pageNumberLocation || 'footer')
  const selectedFormat = ref(store.pageNumberFormat || 'number-only')

  watch(
    () => store.pageNumberLocation,
    newLocation => {
      // Update selectedLocation whenever store value changes
      selectedLocation.value = newLocation || 'footer'
    },
    { immediate: true }
  )

  watch(
    () => store.pageNumberFormat,
    newFormat => {
      // Update selectedFormat whenever store value changes
      selectedFormat.value = newFormat || 'number-only'
    },
    { immediate: true }
  )

  // Also watch for changes in page options directly (in case store doesn't update)
  watch(
    () => {
      const firstPage = store.pages[0]
      return firstPage?.component?.options?.pageNumberLocation
    },
    newLocation => {
      if (newLocation !== undefined && newLocation !== '') {
        selectedLocation.value = newLocation
        // Sync to store if different
        if (store.pageNumberLocation !== newLocation) {
          store.pageNumberLocation = newLocation
        }
      }
    },
    { immediate: true }
  )

  watch(
    () => {
      const firstPage = store.pages[0]
      return firstPage?.component?.options?.pageNumberFormat
    },
    newFormat => {
      if (newFormat !== undefined && newFormat !== '') {
        selectedFormat.value = newFormat
        // Sync to store if different
        if (store.pageNumberFormat !== newFormat) {
          store.pageNumberFormat = newFormat
        }
      }
    },
    { immediate: true }
  )

  watch(
    () => {
      const firstPage = store.pages[0]
      return firstPage?.component?.options?.enablePageNumbering
    },
    newValue => {
      if (newValue !== undefined) {
        // Sync to store if different
        if (store.enablePageNumbering !== newValue) {
          store.enablePageNumbering = newValue
        }
      }
    },
    { immediate: true }
  )

  watch(
    () => headerSection.value?.responsiveStyles?.large?.backgroundColor,
    newColor => {
      bgColorHeader.value = newColor || '#ffffff'
    },
    { immediate: true, deep: true }
  )

  watch(
    () => footerSection.value?.responsiveStyles?.large?.backgroundColor,
    newColor => {
      bgColorFooter.value = newColor || '#ffffff'
    },
    { immediate: true, deep: true }
  )

  watch(
    headerSection,
    newSection => {
      onlyFirstPageHeader.value = newSection?.onlyFirstPage || false
    },
    { immediate: true, deep: true }
  )

  watch(
    footerSection,
    newSection => {
      onlyFirstPageFooter.value = newSection?.onlyFirstPage || false
    },
    { immediate: true, deep: true }
  )

  function onSelectImage(image: any) {
    if (!image) return
    updateSectionOption(ELEMENTS_META.SRC, image.url)
  }

  function onPageNumberingChange(value: boolean) {
    store.updatePageNumberOptions({
      enablePageNumbering: value,
    })
  }

  function handlePageNumberingLocationChange(location: string) {
    selectedLocation.value = location
    store.updatePageNumberOptions({
      pageNumberLocation: location,
    })
  }

  function handlePageNumberingFormatChange(format: string) {
    selectedFormat.value = format
    store.updatePageNumberOptions({
      pageNumberFormat: format,
    })
  }

  function updateHeaderBackgroundColor(color: string) {
    bgColorHeader.value = color
    const sectionId = headerSection.value?.id
    if (sectionId) {
      updateSectionStyles(sectionId, ELEMENTS_STYLES.BACKGROUND_COLOR, color, PAGE_SECTIONS.HEADERS)
    }
  }

  function updateFooterBackgroundColor(color: string) {
    bgColorFooter.value = color
    const sectionId = footerSection.value?.id
    if (sectionId) {
      updateSectionStyles(sectionId, ELEMENTS_STYLES.BACKGROUND_COLOR, color, PAGE_SECTIONS.FOOTERS)
    }
  }

  function updateOnlyFirstPageHeader(value: boolean) {
    onlyFirstPageHeader.value = value
    const sectionId = headerSection.value?.id
    if (sectionId) {
      updateSectionOption('onlyFirstPage', value, sectionId, PAGE_SECTIONS.HEADERS)
    }
  }

  function updateOnlyFirstPageFooter(value: boolean) {
    onlyFirstPageFooter.value = value
    const sectionId = footerSection.value?.id
    if (sectionId) {
      updateSectionOption('onlyFirstPage', value, sectionId, PAGE_SECTIONS.FOOTERS)
    }
  }
</script>

<template>
  <div class="hl-proposal-sidebar-content group-headers-tray mt-2">
    <UITextSmMedium class="mb-2">{{ $t('common.header') }}</UITextSmMedium>
    <UIDivider />
    <ColorPickerComponent
      v-model="bgColorHeader"
      :label="$t('common.backgroundColor')"
      class="mt-6"
      @update:modelValue="updateHeaderBackgroundColor"
    />
    <div class="flex items-center mt-3">
      <UICheckbox
        :id="`${headerSection?.id || 'header'}-only-first-page-header`"
        :checked="onlyFirstPageHeader"
        :type="'default'"
        @update:checked="updateOnlyFirstPageHeader"
        :disabled="!store.isDocumentEditable"
      >
        {{ $t('common.onlyFirstPage') }}
      </UICheckbox>
    </div>
    <UITextSmMedium class="mt-6 mb-2">{{ $t('common.footer') }}</UITextSmMedium>
    <UIDivider />
    <ColorPickerComponent
      v-model="bgColorFooter"
      :label="$t('common.backgroundColor')"
      class="mt-6"
      @update:modelValue="updateFooterBackgroundColor"
    />
    <div class="flex items-center mt-3 mb-2">
      <UICheckbox
        :id="`${footerSection?.id || 'footer'}-only-first-page-footer`"
        :checked="onlyFirstPageFooter"
        :type="'default'"
        @update:checked="updateOnlyFirstPageFooter"
        :disabled="!store.isDocumentEditable"
      >
        {{ $t('common.onlyFirstPage') }}
      </UICheckbox>
    </div>
    <UITextSmMedium class="mt-6 mb-2">{{ $t('common.pageNumberingInPDF') }}</UITextSmMedium>
    <UIDivider />
    <Switch
      :value="store.enablePageNumbering as boolean"
      :label="$t('common.showPageNumbers')"
      @update:value="onPageNumberingChange"
      :disabled="!store.isDocumentEditable"
      class="mt-6"
    />

    <div v-if="showPageNumbers" class="mt-6">
      <UITextSmNormal class="mb-3">{{ $t('common.pageNumberLocation') }}</UITextSmNormal>
      <div class="flex mb-5">
        <UIRadio
          id="header-page-numbering-radio"
          :checked="selectedLocation === 'header'"
          value="header"
          class="mr-6"
          name="page-number-location"
          @change="handlePageNumberingLocationChange('header')"
        >
          {{ $t('common.header') }}
        </UIRadio>

        <UIRadio
          id="footer-page-numbering-radio"
          :checked="selectedLocation === 'footer'"
          value="footer"
          name="page-number-location"
          @change="handlePageNumberingLocationChange('footer')"
        >
          {{ $t('common.footer') }}
        </UIRadio>
      </div>

      <UITextSmNormal class="mb-3">{{ $t('common.pageNumberFormat') }}</UITextSmNormal>
      <div class="flex">
        <UIRadio
          id="page-x-of-y-radio"
          :checked="selectedFormat === 'x-of-y'"
          value="x-of-y"
          class="mr-6"
          name="page-number-format"
          @change="handlePageNumberingFormatChange('x-of-y')"
        >
          {{ $t('common.pageXOfY') }}
        </UIRadio>

        <UIRadio
          id="page-number-only-radio"
          :checked="selectedFormat === 'number-only'"
          value="number-only"
          name="page-number-format"
          @change="handlePageNumberingFormatChange('number-only')"
        >
          {{ $t('common.pageNumbers') }}
        </UIRadio>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  .hl-form-content-hz {
    align-items: center !important;
  }

  .hl-proposal-sidebar-content.group-headers-tray {
    .n-divider.n-divider--no-title {
      margin: 5px 0px;
    }
    /* Collapse form-item feedback wrapper so only-first-page toggle sits closer to color picker */
    .n-form-item-feedback-wrapper {
      margin-top: 0;
      min-height: 0;
    }
  }
</style>
